import React from "react";
import "./style.css";

/**
 * Inline SVG icon set for the landing page.
 *
 * Replaces the emoji previously used as UI icons. Emoji render differently on
 * every OS, cannot inherit brand colour, and cannot animate on hover — all
 * three matter on a product page.
 *
 * Every path is drawn on a 24x24 grid with `currentColor` strokes, so an icon
 * takes its colour from whatever it sits inside. No fills, uniform 1.75 stroke
 * weight, round caps and joins.
 */

const paths = {
  /* ---- Core capability ------------------------------------------------- */

  // brain — AI-first architecture (was 🧠 💡)
  brain: (
    <>
      <path d="M12 4.5a2.5 2.5 0 0 0-5 0 2.5 2.5 0 0 0-1.6 4.4A2.5 2.5 0 0 0 4 11.5a2.5 2.5 0 0 0 1.7 2.4A2.5 2.5 0 0 0 7 18a2.5 2.5 0 0 0 5 .5Z" />
      <path d="M12 4.5a2.5 2.5 0 0 1 5 0 2.5 2.5 0 0 1 1.6 4.4A2.5 2.5 0 0 1 20 11.5a2.5 2.5 0 0 1-1.7 2.4A2.5 2.5 0 0 1 17 18a2.5 2.5 0 0 1-5 .5Z" />
      <path d="M12 4.5v14" />
    </>
  ),

  // rocket — launch / SaaS product build (was 🚀)
  rocket: (
    <>
      <path d="M12 3c3 2 4.5 5.2 4.5 9L12 16l-4.5-4c0-3.8 1.5-7 4.5-9Z" />
      <circle cx="12" cy="9.5" r="1.8" />
      <path d="M7.5 12 5 14.5V18l3-1.5M16.5 12 19 14.5V18l-3-1.5" />
      <path d="M10.5 19.5 12 22l1.5-2.5" />
    </>
  ),

  // sparkles — AI automation / intelligence (was 🤖 ✨)
  sparkles: (
    <>
      <path d="M12 3l1.8 4.7L18.5 9.5l-4.7 1.8L12 16l-1.8-4.7L5.5 9.5l4.7-1.8Z" />
      <path d="M18.5 15l.9 2.1 2.1.9-2.1.9-.9 2.1-.9-2.1-2.1-.9 2.1-.9Z" />
    </>
  ),

  // circuit — AI agent / neural integration
  circuit: (
    <>
      <rect x="8" y="8" width="8" height="8" rx="1.5" />
      <path d="M12 3v5M12 16v5M3 12h5M16 12h5" />
      <circle cx="12" cy="3" r="1.2" />
      <circle cx="12" cy="21" r="1.2" />
      <circle cx="3" cy="12" r="1.2" />
      <circle cx="21" cy="12" r="1.2" />
    </>
  ),

  // layers — platform / multi-tenant architecture
  layers: (
    <>
      <path d="M12 3 3 7.5l9 4.5 9-4.5Z" />
      <path d="M3 12.5 12 17l9-4.5" />
      <path d="M3 17 12 21.5 21 17" />
    </>
  ),

  /* ---- Engineering & operations ---------------------------------------- */

  // wrench — maintenance & support (was 🔧)
  wrench: (
    <>
      <path d="M15.5 3.5a5 5 0 0 0-6 6.4L3.8 15.6a2 2 0 0 0 2.8 2.8l5.7-5.7a5 5 0 0 0 6.4-6l-3 3-2.6-2.6Z" />
    </>
  ),

  // shield — security & privacy (was 🔐)
  shield: (
    <>
      <path d="M12 3 5 6v5.5c0 4.3 2.9 8.1 7 9.3 4.1-1.2 7-5 7-9.3V6Z" />
      <path d="m9.2 12 2 2 3.6-3.8" />
    </>
  ),

  // cloud — cloud infrastructure (was ☁️)
  cloud: (
    <path d="M7.5 18.5a4 4 0 0 1-.4-8 5.2 5.2 0 0 1 9.9 1.1 3.5 3.5 0 0 1-.6 6.9Z" />
  ),

  // bolt — speed / performance (was ⚡)
  bolt: <path d="M13.5 2.5 5 13.5h6l-1 8 8.5-11h-6Z" />,

  // gauge — monitoring & uptime (was 📊 with a metrics sense)
  gauge: (
    <>
      <path d="M4 17a8.5 8.5 0 1 1 16 0" />
      <path d="M12 17 15.5 11" />
      <circle cx="12" cy="17" r="1.3" />
    </>
  ),

  // activity — continuous monitoring / live status (was 📊)
  activity: <path d="M3 12.5h3.5L9 6l3.5 12 2.5-7 1.6 3h4.4" />,

  // trending — growth / long-term product thinking (was 📈)
  trending: (
    <>
      <path d="M3.5 16.5 9.5 10l3.5 3.5L20.5 6" />
      <path d="M15.5 6h5v5" />
    </>
  ),

  /* ---- Industries ------------------------------------------------------- */

  // pulse-heart — healthcare (was 🏥)
  health: (
    <>
      <path d="M12 20s-7-4.4-7-9.3A4.2 4.2 0 0 1 12 7.6a4.2 4.2 0 0 1 7 3.1c0 4.9-7 9.3-7 9.3Z" />
      <path d="M6.5 13h2.8l1.2-2 1.6 3.4 1.2-2h3.2" />
    </>
  ),

  // finance — fintech (was 💰)
  finance: (
    <>
      <circle cx="12" cy="12" r="8.5" />
      <path d="M12 7v10M14.6 9.4a2.8 2.8 0 0 0-2.6-1.3c-1.6 0-2.6.9-2.6 2.1 0 3 5.4 1.5 5.4 4.4 0 1.3-1.1 2.2-2.8 2.2a3 3 0 0 1-2.8-1.5" />
    </>
  ),

  // truck — logistics (was 🚚)
  truck: (
    <>
      <path d="M2.5 7h10v9h-10Z" />
      <path d="M12.5 10.5H17l3 3V16h-7.5Z" />
      <circle cx="6.5" cy="18" r="1.8" />
      <circle cx="16.5" cy="18" r="1.8" />
    </>
  ),

  // graduation — education (was 🎓)
  education: (
    <>
      <path d="M12 4 2.5 8.5 12 13l9.5-4.5Z" />
      <path d="M6.5 10.8V16c0 1.4 2.5 2.6 5.5 2.6s5.5-1.2 5.5-2.6v-5.2" />
      <path d="M21.5 8.5v5" />
    </>
  ),

  /* ---- Support & communication ------------------------------------------ */

  // clock — 24/7 help desk (was ⏰)
  clock: (
    <>
      <circle cx="12" cy="12" r="8.5" />
      <path d="M12 7v5.3l3.4 2" />
    </>
  ),

  // user — dedicated account manager (was 👤)
  user: (
    <>
      <circle cx="12" cy="8.5" r="3.6" />
      <path d="M5 19.5a7 7 0 0 1 14 0" />
    </>
  ),

  // chat — WhatsApp + email support (was 💬)
  chat: (
    <path d="M20 12.2c0 3.7-3.6 6.7-8 6.7a9.4 9.4 0 0 1-2.6-.4L4.5 20l1.3-3.3a6.3 6.3 0 0 1-1.8-4.5c0-3.7 3.6-6.7 8-6.7s8 3 8 6.7Z" />
  ),

  // ticket — ticket tracking (was 🎫)
  ticket: (
    <>
      <path d="M3.5 8.5A1.5 1.5 0 0 1 5 7h14a1.5 1.5 0 0 1 1.5 1.5v1.8a2 2 0 0 0 0 3.4v1.8A1.5 1.5 0 0 1 19 17H5a1.5 1.5 0 0 1-1.5-1.5v-1.8a2 2 0 0 0 0-3.4Z" />
      <path d="M13.5 7v10" strokeDasharray="1.5 2" />
    </>
  ),

  // clipboard — SLA commitment (was 📋)
  clipboard: (
    <>
      <path d="M9 4.5h6v2.2H9Z" />
      <path d="M15 5.6h2.5v14H6.5v-14H9" />
      <path d="M9.2 11.5h5.6M9.2 15h3.8" />
    </>
  ),

  // phone — discovery call (was 📞)
  phone: (
    <path d="M7.5 3.5 9.8 8l-2 1.8a11 11 0 0 0 5.4 5.4l1.8-2 4.5 2.3v3a1.5 1.5 0 0 1-1.7 1.5C10.9 19.3 4.7 13.1 3.7 5.2A1.5 1.5 0 0 1 5.2 3.5Z" />
  ),

  // handshake — partnership / dedicated support (was 🤝)
  handshake: (
    <>
      <path d="M3 9.5 6.5 7l3.2 2.6a2 2 0 0 0 2.6 0L15.5 7 21 9.5" />
      <path d="M6.5 7v8.5L12 19l5.5-3.5V7" />
      <path d="M12 12.5v6.5" />
    </>
  ),

  /* ---- Product & UI ------------------------------------------------------ */

  // check — feature list marks (was ✓)
  check: <path d="M4.5 12.5 9.5 17.5 19.5 6.5" />,

  // arrow-right — card CTAs (was →)
  arrowRight: (
    <>
      <path d="M4 12h15" />
      <path d="m13.5 6.5 6 5.5-6 5.5" />
    </>
  ),

  // globe — global reach (replaces the 🇮🇳 / 📍 location signals)
  globe: (
    <>
      <circle cx="12" cy="12" r="8.5" />
      <path d="M3.5 12h17" />
      <path d="M12 3.5a13 13 0 0 1 0 17 13 13 0 0 1 0-17Z" />
    </>
  ),

  // users — team (was 👥)
  users: (
    <>
      <circle cx="9.5" cy="8.5" r="3.2" />
      <path d="M3.5 19a6 6 0 0 1 12 0" />
      <path d="M16 5.6a3.2 3.2 0 0 1 0 5.8M17.5 14.2a6 6 0 0 1 3 4.8" />
    </>
  ),

  // code — engineering
  code: <path d="m8.5 8.5-4 3.5 4 3.5M15.5 8.5l4 3.5-4 3.5M13.5 5.5l-3 13" />,

  // database — data / storage
  database: (
    <>
      <ellipse cx="12" cy="6.5" rx="7.5" ry="3" />
      <path d="M4.5 6.5v11c0 1.7 3.4 3 7.5 3s7.5-1.3 7.5-3v-11" />
      <path d="M4.5 12c0 1.7 3.4 3 7.5 3s7.5-1.3 7.5-3" />
    </>
  ),

  // mobile — phone / cross-platform app
  mobile: (
    <>
      <rect x="6.5" y="2.5" width="11" height="19" rx="2.2" />
      <path d="M10.8 18.5h2.4" />
    </>
  ),

  // monitor — web app / dashboard on a desktop screen
  monitor: (
    <>
      <rect x="2.5" y="3.5" width="19" height="13" rx="1.8" />
      <path d="M8 21h8M12 16.5V21" />
    </>
  ),

  // responsive — desktop + phone, adaptive UI
  responsive: (
    <>
      <rect x="2" y="4" width="13" height="10" rx="1.5" />
      <path d="M6 18h5" />
      <rect x="16" y="9" width="6" height="11" rx="1.5" />
    </>
  ),

  // dashboard — admin panel / grid of widgets
  dashboard: (
    <>
      <rect x="3" y="3" width="7.5" height="7.5" rx="1.2" />
      <rect x="13.5" y="3" width="7.5" height="4.5" rx="1.2" />
      <rect x="3" y="13.5" width="7.5" height="7.5" rx="1.2" />
      <rect x="13.5" y="10.5" width="7.5" height="10.5" rx="1.2" />
    </>
  ),

  // cart — e-commerce
  cart: (
    <>
      <path d="M6 2.5 3 6.5V20a1.8 1.8 0 0 0 1.8 1.8h14.4A1.8 1.8 0 0 0 21 20V6.5L18 2.5Z" />
      <path d="M3 6.5h18" />
      <path d="M16 10.5a4 4 0 0 1-8 0" />
    </>
  ),

  // chart — analytics / performance bars
  chart: <path d="M18 20.5V10M12 20.5V3.5M6 20.5v-6" />,

  // plug — API / system integration
  plug: (
    <>
      <path d="M9 2.5v6M15 2.5v6" />
      <path d="M6 8.5h12v3a6 6 0 0 1-12 0Z" />
      <path d="M12 17.5v4" />
    </>
  ),

  // document — file / knowledge extraction
  document: (
    <>
      <path d="M14 2.5H6.5v19h11V8Z" />
      <path d="M14 2.5V8h3.5" />
      <path d="M8.8 12.5h6.4M8.8 16h4.4" />
    </>
  ),

  // search — semantic discovery
  search: (
    <>
      <circle cx="11" cy="11" r="6.5" />
      <path d="M20 20l-4.4-4.4" />
    </>
  ),

  // workflow — orchestration / LangGraph pipelines
  workflow: (
    <>
      <rect x="3" y="3.5" width="6" height="5" rx="1.2" />
      <rect x="15" y="15.5" width="6" height="5" rx="1.2" />
      <rect x="9" y="9.5" width="6" height="5" rx="1.2" />
      <path d="M9 6h3a1.5 1.5 0 0 1 1.5 1.5v2M15 18h-3a1.5 1.5 0 0 1-1.5-1.5v-2" />
    </>
  ),

  // target — precision / outcomes
  target: (
    <>
      <circle cx="12" cy="12" r="8.5" />
      <circle cx="12" cy="12" r="4.5" />
      <circle cx="12" cy="12" r="1.2" />
    </>
  ),
};

export const iconNames = Object.keys(paths);

/**
 * @param {Object} props
 * @param {string} props.name        Key from the icon set above.
 * @param {number} [props.size=24]   Rendered px size (width and height).
 * @param {string} [props.accent]    Accent variant: indigo | blue | green | amber | violet.
 * @param {string} [props.className] Extra classes.
 * @param {string} [props.title]     Accessible label. Omit for decorative icons,
 *                                   which are then hidden from assistive tech.
 */
export default function Icon({
  name,
  size = 24,
  accent,
  className = "",
  title,
  strokeWidth = 1.75,
  ...rest
}) {
  const path = paths[name];

  if (!path) {
    if (process.env.NODE_ENV !== "production") {
      console.warn(`<Icon />: unknown icon "${name}"`);
    }
    return null;
  }

  const classes = [
    "bct-icon",
    accent ? `bct-icon--${accent}` : "",
    className,
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <svg
      className={classes}
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
      role={title ? "img" : undefined}
      aria-hidden={title ? undefined : true}
      focusable="false"
      {...rest}
    >
      {title ? <title>{title}</title> : null}
      {path}
    </svg>
  );
}
